import { useState, useEffect } from "react";
import { Trash2, Plus, RefreshCw, Star, Eye, EyeOff, Check, ExternalLink, Loader2, Search, ToggleLeft, ToggleRight, PackagePlus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

var API = "/api";

var STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "interesting", label: "Starred" },
  { value: "have", label: "Have" },
  { value: "ignored", label: "Ignored" }
];

var SOURCE_LABELS = {
  github: "GitHub",
  reddit: "Reddit",
  thingiverse: "Thingiverse",
  printables: "Printables",
  grabcad: "GrabCAD",
  yeggi: "Yeggi",
  cults3d: "Cults3D",
  google: "Google",
  brave: "Brave"
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  var d = new Date(dateStr);
  return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function StatusBadge({ status }) {
  var styles = {
    new: "bg-blue-500/20 text-blue-300 border-blue-500/40",
    interesting: "bg-[#C0A062]/20 text-[#C0A062] border-[#C0A062]/40",
    have: "bg-green-500/20 text-green-300 border-green-500/40",
    ignored: "bg-[#3f3f46]/40 text-[#71717a] border-white/10"
  };
  return (
    <span className={"px-2 py-0.5 text-[10px] font-mono uppercase rounded border " + (styles[status] || styles.new)} data-testid={"status-badge-" + status}>
      {status || "new"}
    </span>
  );
}

export default function DiscoveryTab({ onCreateListing }) {
  var [status, setStatus] = useState(null);
  var [keywords, setKeywords] = useState([]);
  var [results, setResults] = useState([]);
  var [counts, setCounts] = useState({});
  var [runs, setRuns] = useState([]);
  var [newKeyword, setNewKeyword] = useState("");
  var [statusFilter, setStatusFilter] = useState("all");
  var [sourceFilter, setSourceFilter] = useState("all");
  var [searchTerm, setSearchTerm] = useState("");
  var [loading, setLoading] = useState(false);
  var [scanning, setScanning] = useState(false);

  async function refreshAll() {
    setLoading(true);
    try {
      var [statusRes, kwRes, runsRes] = await Promise.all([
        axios.get(API + "/discovery/status"),
        axios.get(API + "/discovery/keywords"),
        axios.get(API + "/discovery/runs?limit=5")
      ]);
      setStatus(statusRes.data);
      setKeywords(kwRes.data || []);
      setRuns(runsRes.data || []);
      await refreshResults();
    } catch (e) {
      toast.error("Failed to load Discovery data");
    } finally {
      setLoading(false);
    }
  }

  async function refreshResults() {
    var params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (sourceFilter !== "all") params.source = sourceFilter;
    if (searchTerm.trim()) params.keyword = searchTerm.trim();
    try {
      var res = await axios.get(API + "/discovery/results", { params: params });
      setResults(res.data.items || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      toast.error("Failed to load results");
    }
  }

  useEffect(function() {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function() {
    refreshResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, sourceFilter]);

  async function addKeyword() {
    var kw = newKeyword.trim();
    if (!kw) return;
    try {
      await axios.post(API + "/discovery/keywords", { keyword: kw, enabled: true });
      setNewKeyword("");
      toast.success("Keyword added");
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add keyword");
    }
  }

  async function toggleKeyword(kw) {
    try {
      await axios.patch(API + "/discovery/keywords/" + kw.id, { keyword: kw.keyword, enabled: !kw.enabled });
      refreshAll();
    } catch {
      toast.error("Failed to update keyword");
    }
  }

  async function deleteKeyword(id) {
    if (!window.confirm("Delete this keyword? Existing results remain.")) return;
    try {
      await axios.delete(API + "/discovery/keywords/" + id);
      toast.success("Keyword removed");
      refreshAll();
    } catch {
      toast.error("Failed to delete keyword");
    }
  }

  async function runScan() {
    if (scanning) return;
    setScanning(true);
    try {
      var res = await axios.post(API + "/discovery/run", {});
      var s = res.data;
      var vouchedMsg = s.vouched ? " · ★" + s.vouched + " auto-vouched" : "";
      toast.success("Scan complete — " + s.total_new + " new, " + s.total_seen_again + " seen again" + vouchedMsg);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  }

  async function setResultStatus(rid, newStatus) {
    try {
      await axios.patch(API + "/discovery/results/" + rid, { status: newStatus });
      refreshResults();
      refreshAll();
    } catch {
      toast.error("Failed to update result");
    }
  }

  async function removeResult(rid) {
    if (!window.confirm("Remove this result from the list?")) return;
    try {
      await axios.delete(API + "/discovery/results/" + rid);
      refreshResults();
    } catch {
      toast.error("Failed to delete");
    }
  }

  var enabledKeywordsCount = keywords.filter(function(k) { return k.enabled; }).length;
  var cooldownMin = status ? Math.ceil(status.cooldown_remaining_seconds / 60) : 0;
  var canRun = status?.can_run_now && enabledKeywordsCount > 0 && !scanning;

  return (
    <div className="space-y-6" data-testid="discovery-tab">
      {/* Header */}
      <div className="bg-[#121212] border border-white/5 rounded-sm p-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-heading font-bold text-white text-lg uppercase">Parts Discovery</h3>
            <p className="text-[#71717a] text-sm mt-1 max-w-2xl">
              Hunts public sources (GitHub, Reddit, Thingiverse, Printables) for 3D model files matching your keywords. Stores links only — no files are downloaded.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="border-white/10 text-[#A1A1AA] hover:text-white"
              onClick={refreshAll}
              disabled={loading}
              data-testid="refresh-discovery-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
            <Button
              className="btn-primary"
              onClick={runScan}
              disabled={!canRun}
              data-testid="run-discovery-scan-btn"
              title={!status?.can_run_now ? "Cooldown active" : (enabledKeywordsCount === 0 ? "Add at least one keyword" : "Run a scan now")}
            >
              {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              {scanning ? "Scanning..." : "Run Scan Now"}
            </Button>
          </div>
        </div>

        {status && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            <div className="bg-black/40 border border-white/5 rounded-sm p-3">
              <p className="text-[10px] font-mono text-[#71717a] uppercase">Keywords</p>
              <p className="text-white text-xl font-heading">{enabledKeywordsCount}<span className="text-[#71717a] text-sm font-mono">/{status.keywords_total}</span></p>
            </div>
            <div className="bg-black/40 border border-white/5 rounded-sm p-3">
              <p className="text-[10px] font-mono text-[#71717a] uppercase">Results</p>
              <p className="text-white text-xl font-heading">{status.results_total}</p>
            </div>
            <div className="bg-black/40 border border-white/5 rounded-sm p-3">
              <p className="text-[10px] font-mono text-[#71717a] uppercase">New / Starred</p>
              <p className="text-white text-xl font-heading">{status.results_new}<span className="text-[#C0A062] text-sm font-mono ml-2">★{status.results_interesting}</span></p>
            </div>
            <div className="bg-black/40 border border-white/5 rounded-sm p-3">
              <p className="text-[10px] font-mono text-[#71717a] uppercase">Next Scan</p>
              <p className="text-white text-xl font-heading">
                {status.can_run_now ? "Ready" : (cooldownMin >= 60 ? Math.floor(cooldownMin/60) + "h " + (cooldownMin%60) + "m" : cooldownMin + "m")}
              </p>
            </div>
          </div>
        )}

        {status && (
          <div className="flex flex-wrap gap-2 mt-4">
            {Object.keys(status.sources_available || {}).map(function(src) {
              var on = status.sources_available[src];
              return (
                <span key={src} className={"px-2 py-1 text-[10px] font-mono uppercase rounded border " + (on ? "bg-green-500/10 text-green-300 border-green-500/30" : "bg-white/5 text-[#71717a] border-white/10")}>
                  {SOURCE_LABELS[src] || src} {on ? "•" : "○"}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Keywords */}
      <div className="bg-[#121212] border border-white/5 rounded-sm p-5">
        <h4 className="font-heading font-bold text-white uppercase mb-3">Keywords</h4>
        <p className="text-[#71717a] text-xs mb-4">Add specific part names. Examples: "Type 99 Arisaka bolt", "Hakim gas piston", "MP-35 Bergmann firing pin", "Maxim MG08 lock". More specific = better signal.</p>
        <div className="flex gap-2 mb-4">
          <Input
            value={newKeyword}
            onChange={function(e) { setNewKeyword(e.target.value); }}
            onKeyDown={function(e) { if (e.key === "Enter") addKeyword(); }}
            placeholder="e.g. Type 99 Arisaka firing pin"
            className="input-tactical"
            data-testid="new-keyword-input"
          />
          <Button className="btn-primary" onClick={addKeyword} disabled={!newKeyword.trim()} data-testid="add-keyword-btn">
            <Plus className="w-4 h-4 mr-2" /> Add
          </Button>
        </div>
        {keywords.length === 0 ? (
          <p className="text-[#71717a] text-sm italic">No keywords yet. Add some above to start hunting.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {keywords.map(function(k) {
              return (
                <div key={k.id} className={"flex items-center gap-2 px-3 py-1.5 rounded border text-sm " + (k.enabled ? "bg-[#C0A062]/10 border-[#C0A062]/40 text-white" : "bg-white/5 border-white/10 text-[#71717a]")} data-testid={"keyword-chip-" + k.id}>
                  <button onClick={function() { toggleKeyword(k); }} title={k.enabled ? "Disable" : "Enable"} data-testid={"toggle-keyword-" + k.id}>
                    {k.enabled ? <ToggleRight className="w-4 h-4 text-[#C0A062]" /> : <ToggleLeft className="w-4 h-4" />}
                  </button>
                  <span>{k.keyword}</span>
                  <button onClick={function() { deleteKeyword(k.id); }} className="text-[#71717a] hover:text-red-400" data-testid={"delete-keyword-" + k.id}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-[#121212] border border-white/5 rounded-sm p-5">
        <div className="flex items-end flex-wrap gap-3 mb-4">
          <div>
            <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Status</Label>
            <div className="flex gap-1 flex-wrap">
              {STATUS_OPTIONS.map(function(o) {
                var active = statusFilter === o.value;
                var count = o.value === "all" ? results.length : (counts[o.value] || 0);
                return (
                  <button
                    key={o.value}
                    onClick={function() { setStatusFilter(o.value); }}
                    className={"px-3 py-1.5 text-xs font-mono uppercase rounded border " + (active ? "bg-[#C0A062] text-black border-[#C0A062]" : "bg-black/40 text-[#A1A1AA] border-white/10 hover:border-[#C0A062]/40")}
                    data-testid={"status-filter-" + o.value}
                  >
                    {o.label} <span className="opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Source</Label>
            <div className="flex gap-1 flex-wrap">
              {["all", "github", "reddit", "thingiverse", "printables", "grabcad", "yeggi", "cults3d", "google", "brave"].map(function(s) {
                var active = sourceFilter === s;
                return (
                  <button
                    key={s}
                    onClick={function() { setSourceFilter(s); }}
                    className={"px-3 py-1.5 text-xs font-mono uppercase rounded border " + (active ? "bg-[#C0A062] text-black border-[#C0A062]" : "bg-black/40 text-[#A1A1AA] border-white/10 hover:border-[#C0A062]/40")}
                    data-testid={"source-filter-" + s}
                  >
                    {s === "all" ? "All" : (SOURCE_LABELS[s] || s)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Search Keyword</Label>
            <div className="flex gap-2">
              <Input
                value={searchTerm}
                onChange={function(e) { setSearchTerm(e.target.value); }}
                onKeyDown={function(e) { if (e.key === "Enter") refreshResults(); }}
                placeholder="Filter by keyword text..."
                className="input-tactical"
                data-testid="search-keyword-input"
              />
              <Button variant="outline" className="border-white/10 text-[#A1A1AA]" onClick={refreshResults} data-testid="apply-search-btn">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Results table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-3 font-mono text-xs text-[#A1A1AA] uppercase">Source</th>
                <th className="text-left p-3 font-mono text-xs text-[#A1A1AA] uppercase">Result</th>
                <th className="text-left p-3 font-mono text-xs text-[#A1A1AA] uppercase">Keyword</th>
                <th className="text-left p-3 font-mono text-xs text-[#A1A1AA] uppercase">Status</th>
                <th className="text-left p-3 font-mono text-xs text-[#A1A1AA] uppercase">Found</th>
                <th className="text-right p-3 font-mono text-xs text-[#A1A1AA] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-[#71717a]">
                  {status && status.results_total === 0 ? "No results yet. Add keywords and run your first scan." : "No results match the current filters."}
                </td></tr>
              )}
              {results.map(function(r) {
                return (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/5" data-testid={"discovery-result-" + r.id}>
                    <td className="p-3">
                      <span className="px-2 py-0.5 text-[10px] font-mono uppercase rounded bg-white/5 border border-white/10 text-[#A1A1AA]">
                        {SOURCE_LABELS[r.source] || r.source}
                      </span>
                    </td>
                    <td className="p-3 max-w-md">
                      <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#C0A062] inline-flex items-center gap-1.5 break-words" data-testid={"discovery-result-link-" + r.id}>
                        <span className="line-clamp-2">{r.title || r.url}</span>
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                      {r.vouches && r.vouches.length > 0 && (
                        <p className="text-[10px] font-mono text-[#C0A062] mt-0.5" data-testid={"vouches-" + r.id}>
                          ✓ Vouched by: {r.vouches.join(", ")}
                        </p>
                      )}
                      {r.snippet && <p className="text-[#71717a] text-xs mt-0.5 line-clamp-2">{r.snippet}</p>}
                    </td>
                    <td className="p-3 text-[#A1A1AA] text-sm font-mono">{r.keyword}</td>
                    <td className="p-3"><StatusBadge status={r.status} /></td>
                    <td className="p-3 text-[#71717a] text-xs">{formatDate(r.first_seen)}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        {onCreateListing && (
                          <button title="Pre-fill new product listing from this result" onClick={function() { onCreateListing(r); }} className="p-1.5 rounded hover:bg-[#C0A062]/20 text-[#C0A062]" data-testid={"create-listing-" + r.id}>
                            <PackagePlus className="w-4 h-4" />
                          </button>
                        )}
                        <button title="Star (interesting)" onClick={function() { setResultStatus(r.id, "interesting"); }} className="p-1.5 rounded hover:bg-[#C0A062]/20 text-[#C0A062]" data-testid={"star-result-" + r.id}>
                          <Star className="w-4 h-4" fill={r.status === "interesting" ? "currentColor" : "none"} />
                        </button>
                        <button title="Mark as have" onClick={function() { setResultStatus(r.id, "have"); }} className="p-1.5 rounded hover:bg-green-500/20 text-green-400" data-testid={"have-result-" + r.id}>
                          <Check className="w-4 h-4" />
                        </button>
                        <button title="Ignore" onClick={function() { setResultStatus(r.id, "ignored"); }} className="p-1.5 rounded hover:bg-white/10 text-[#71717a]" data-testid={"ignore-result-" + r.id}>
                          <EyeOff className="w-4 h-4" />
                        </button>
                        <button title="Reset to new" onClick={function() { setResultStatus(r.id, "new"); }} className="p-1.5 rounded hover:bg-blue-500/20 text-blue-300" data-testid={"reset-result-" + r.id}>
                          <Eye className="w-4 h-4" />
                        </button>
                        <button title="Delete" onClick={function() { removeResult(r.id); }} className="p-1.5 rounded hover:bg-red-500/20 text-red-400" data-testid={"delete-result-" + r.id}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Run history */}
      {runs.length > 0 && (
        <div className="bg-[#121212] border border-white/5 rounded-sm p-5">
          <h4 className="font-heading font-bold text-white uppercase mb-3">Recent Scans</h4>
          <div className="space-y-2">
            {runs.map(function(r) {
              return (
                <div key={r.id || r.ran_at} className="flex items-center justify-between text-xs text-[#A1A1AA] font-mono border-b border-white/5 pb-2 last:border-0">
                  <span>{formatDate(r.ran_at)}</span>
                  <span>{(r.keywords || []).length} keywords</span>
                  <span className="text-green-400">+{r.total_new} new</span>
                  <span className="text-[#71717a]">{r.total_seen_again} repeats</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
